// Tema de ambiente elegible por el usuario ("Cómo se ve MiPAE", 2026-09-08).
//
// La fase 1 (2026-09-08, mismo día) solo cambiaba el ACENTO: botones y
// chips. El fundador lo vio y dijo "cambia muy poco, se referían a cambiar
// la temática completa". Esta fase la REEMPLAZA: el acento pasa a ser una
// pieza más de un TEMA completo (papel, superficies, bordes, tinta y
// acento, con su versión clara y oscura). Ese día no conviven dos
// selectores: `acento.js`/`AcentoSelector.jsx` se renombraron a este archivo.
//
// El mismo día (más tarde) el fundador pidió cuatro temas más: Celeste,
// Café (su favorito), Rosado (color muy típico de la profesión) y Morado.
// El catálogo queda en 9. Morado usa un violeta como acento de ambiente;
// no es lo mismo que `clinical-pending` (también violeta, semáforo clínico
// que NO cambia con el tema, ver `VARIABLES_TEMA`).
//
// Por qué 5 looks CERRADOS y no un selector libre: un color sin contraste
// suficiente deja texto o botones ilegibles. Cada tema de este catálogo trae
// su contraste WCAG AA fijado por test (claro y oscuro), así que cualquier
// combinación que el usuario elija sigue siendo legible.
//
// Por qué el tema default ('mipae') no escribe NINGUNA variable: `index.css`
// sigue siendo la única fuente de verdad del ambiente de marca. Este módulo
// solo SOBRESCRIBE con variables inline en <html> cuando el usuario eligió
// algo distinto del default; sin elección, index.css manda exactamente como
// hoy.
//
// Qué queda FIJO y por qué (regla del fundador: "el color es urgencia, no
// identidad"): los semáforos clínicos `clinical-*`, los colores de turno
// `shift-*`, la identidad de color por MÓDULO `--module-*` (es
// reconocimiento, no ambiente: cambiar de tema no debe hacer que Escalas o
// Enciclopedia dejen de reconocerse), `system-*`, `--priority*`, `--plus*`
// y el marino de marca `--brand`/`--brand-text` del encabezado. Ninguno de
// esos tokens aparece en `VARIABLES_TEMA`.
//
// EXCEPCIÓN del 2026-09-19, decidida por el fundador: el LOGO sí sigue al
// tema (`--marca-*` y `--sello-*`). No es aflojar la regla de arriba, es
// terminar de aplicarla: la palabra "MiPAE" del encabezado ya se teñía por
// `--color-brand` mientras las dos figuras de al lado no, así que el logo se
// pintaba a medias, y eso se ve peor que no teñirlo. El color por MÓDULO
// sigue fijo, que es lo que sostiene el reconocimiento entre módulos, y el
// logo que sale hacia afuera (Play, correos, material de tienda) son imágenes
// estáticas que ningún tema toca.

export const TEMA_DEFAULT = 'mipae';
export const CLAVE_TEMA = 'mipae_tema';

// Cada tema trae 13 hex base por modo: paper, paperWarm, surface, surface2,
// edge, edgeStrong, ink, inkMuted, inkSubtle, inkFaint, accent, accentHover,
// accentLight. `mipae` es espejo EXACTO de lo que hoy vive en index.css
// (líneas 105-140 y 441-531 en :root/.dark); si la marca cambia, este bloque
// se actualiza a la par. Los otros 8 (papel/clinica/bosque/grafito y los
// cuatro del 2026-09-08: celeste/cafe/rosado/morado) fueron diseñados y
// validados por contraste antes de sumarse al catálogo.
export const TEMAS = [
  {
    id: 'mipae',
    nombre: 'MiPAE',
    claro: {
      paper: '#FFFFFF', paperWarm: '#F8F5F1', surface: '#F8FAFC', surface2: '#F1F5F9',
      edge: '#E2E8F0', edgeStrong: '#94A3B8', ink: '#0F172A', inkMuted: '#64748B',
      inkSubtle: '#526070', inkFaint: '#94A3B8', accent: '#4A5FA6', accentHover: '#3D4D7A',
      accentLight: '#6A7BA8',
    },
    oscuro: {
      paper: '#070D1A', paperWarm: '#0D1526', surface: '#0D1526', surface2: '#111F35',
      edge: '#1E3254', edgeStrong: '#243D6B', ink: '#DCE3ED', inkMuted: '#8FA5BC',
      inkSubtle: '#607892', inkFaint: '#4A6385', accent: '#6A7BA8', accentHover: '#8094C5',
      accentLight: '#8094C5',
    },
  },
  {
    id: 'papel',
    nombre: 'Papel',
    claro: {
      paper: '#F7F2E9', paperWarm: '#F1EADF', surface: '#EFE8DC', surface2: '#E6DDCE',
      edge: '#DDD2C1', edgeStrong: '#9E9180', ink: '#2A231B', inkMuted: '#6B5F52',
      inkSubtle: '#5A4F43', inkFaint: '#A89C88', accent: '#A5522E', accentHover: '#86401F',
      accentLight: '#C27A5B',
    },
    oscuro: {
      paper: '#1A1612', paperWarm: '#211C17', surface: '#221D18', surface2: '#2B251F',
      edge: '#3A322A', edgeStrong: '#4E4438', ink: '#EDE4D6', inkMuted: '#B3A695',
      inkSubtle: '#948776', inkFaint: '#5C5147', accent: '#D9946B', accentHover: '#E6AA88',
      accentLight: '#E6AA88',
    },
  },
  {
    id: 'clinica',
    nombre: 'Clínica',
    claro: {
      paper: '#FAFCFD', paperWarm: '#F4F8FA', surface: '#F0F5F8', surface2: '#E5EDF2',
      edge: '#D5DFE6', edgeStrong: '#8A9EAD', ink: '#0C1F2E', inkMuted: '#4E6A79',
      inkSubtle: '#465F6E', inkFaint: '#8A9EAD', accent: '#0F766E', accentHover: '#0B5C56',
      accentLight: '#3E9A92',
    },
    oscuro: {
      paper: '#0A1418', paperWarm: '#0E1B21', surface: '#10202A', surface2: '#162A36',
      edge: '#23404F', edgeStrong: '#2F5266', ink: '#DDE8EE', inkMuted: '#92AAB8',
      inkSubtle: '#7591A1', inkFaint: '#3E5B6B', accent: '#5EC4B6', accentHover: '#7FD6CA',
      accentLight: '#7FD6CA',
    },
  },
  {
    id: 'bosque',
    nombre: 'Bosque',
    claro: {
      paper: '#F5F7F2', paperWarm: '#EFF3EA', surface: '#ECF0E6', surface2: '#E1E7D9',
      edge: '#D1DAC7', edgeStrong: '#86957A', ink: '#1B2A1F', inkMuted: '#5A6956',
      inkSubtle: '#4A5946', inkFaint: '#8C9A80', accent: '#2F6B4F', accentHover: '#24533D',
      accentLight: '#5A9077',
    },
    oscuro: {
      paper: '#0E1512', paperWarm: '#131C17', surface: '#15201A', surface2: '#1C2A22',
      edge: '#2B3E32', edgeStrong: '#3A5242', ink: '#DCE6DD', inkMuted: '#9AB0A0',
      inkSubtle: '#7A9282', inkFaint: '#3A5242', accent: '#6FBF97', accentHover: '#8DD0AE',
      accentLight: '#8DD0AE',
    },
  },
  {
    id: 'grafito',
    nombre: 'Grafito',
    claro: {
      paper: '#FAFAFA', paperWarm: '#F5F5F5', surface: '#F2F2F2', surface2: '#E9E9E9',
      edge: '#DBDBDB', edgeStrong: '#8F8F8F', ink: '#171717', inkMuted: '#636363',
      inkSubtle: '#525252', inkFaint: '#9A9A9A', accent: '#3A3A3A', accentHover: '#1F1F1F',
      accentLight: '#6B6B6B',
    },
    oscuro: {
      paper: '#121212', paperWarm: '#171717', surface: '#1A1A1A', surface2: '#232323',
      edge: '#333333', edgeStrong: '#474747', ink: '#E8E8E8', inkMuted: '#A8A8A8',
      inkSubtle: '#8A8A8A', inkFaint: '#474747', accent: '#C9C9C9', accentHover: '#DADADA',
      accentLight: '#DADADA',
    },
  },
  {
    id: 'celeste',
    nombre: 'Celeste',
    claro: {
      paper: '#F3F9FD', paperWarm: '#EDF5FB', surface: '#E8F3FA', surface2: '#DCEBF5',
      edge: '#C9DEEB', edgeStrong: '#6F97B2', ink: '#0F2A3F', inkMuted: '#4A6A80',
      inkSubtle: '#3D5C72', inkFaint: '#7FA5BE', accent: '#1B6FA8', accentHover: '#155882',
      accentLight: '#3F8FC8',
    },
    oscuro: {
      paper: '#0A1620', paperWarm: '#0E1C28', surface: '#10202E', surface2: '#16293A',
      edge: '#24405A', edgeStrong: '#2F5673', ink: '#DDEAF4', inkMuted: '#93B1C8',
      inkSubtle: '#7594AC', inkFaint: '#3E5B75', accent: '#6BB8EA', accentHover: '#8CCBF3',
      accentLight: '#8CCBF3',
    },
  },
  {
    id: 'cafe',
    nombre: 'Café',
    claro: {
      paper: '#F5EFE8', paperWarm: '#F0E8DF', surface: '#EDE4DA', surface2: '#E3D8CB',
      edge: '#D8CBBB', edgeStrong: '#9B8672', ink: '#2B1D12', inkMuted: '#6A5541',
      inkSubtle: '#5A4736', inkFaint: '#A08C78', accent: '#6B4423', accentHover: '#4E301A',
      accentLight: '#8F6A48',
    },
    oscuro: {
      paper: '#1A130E', paperWarm: '#1F1712', surface: '#221A13', surface2: '#2C221A',
      edge: '#3D3025', edgeStrong: '#54443A', ink: '#EFE5DA', inkMuted: '#B8A48F',
      inkSubtle: '#9A8069', inkFaint: '#5C4A3C', accent: '#C89A6E', accentHover: '#D8B08A',
      accentLight: '#D8B08A',
    },
  },
  {
    id: 'rosado',
    nombre: 'Rosado',
    claro: {
      paper: '#FCF5F7', paperWarm: '#F9EEF2', surface: '#F7EBEF', surface2: '#F1DFE5',
      edge: '#E9CFD8', edgeStrong: '#B78B9A', ink: '#33141F', inkMuted: '#6E4D58',
      inkSubtle: '#5D3E4A', inkFaint: '#B78B9A', accent: '#B2386A', accentHover: '#8F2A55',
      accentLight: '#CC6A91',
    },
    oscuro: {
      paper: '#1B0F14', paperWarm: '#211318', surface: '#24161B', surface2: '#2E1D24',
      edge: '#43303A', edgeStrong: '#5A4049', ink: '#F2E2E8', inkMuted: '#C2A0AC',
      inkSubtle: '#A3808C', inkFaint: '#5F4650', accent: '#E38BB0', accentHover: '#EDA6C3',
      accentLight: '#EDA6C3',
    },
  },
  {
    id: 'morado',
    nombre: 'Morado',
    claro: {
      paper: '#F7F4FC', paperWarm: '#F2EDF9', surface: '#EFEAF8', surface2: '#E6DFF3',
      edge: '#D8CFEA', edgeStrong: '#9A8BB8', ink: '#1F1533', inkMuted: '#5E5175',
      inkSubtle: '#4E4264', inkFaint: '#9A8BB8', accent: '#6A3FB5', accentHover: '#52309C',
      accentLight: '#8D6CCB',
    },
    oscuro: {
      paper: '#120E1C', paperWarm: '#171223', surface: '#191327', surface2: '#211A31',
      edge: '#322947', edgeStrong: '#4C3F69', ink: '#E8E1F3', inkMuted: '#ADA0C6',
      inkSubtle: '#8E80A9', inkFaint: '#4A3F62', accent: '#B79BE8', accentHover: '#C9B3F0',
      accentLight: '#C9B3F0',
    },
  },
  // Los CINCO del 2026-09-19 ("dale todos"): se eligieron por los huecos
  // REALES de la rueda de color, no por gusto, para que ninguno se confunda con
  // los diez que ya estaban. Cada uno pasa los mismos umbrales de contraste del
  // resto MÁS el piso de 3:1 del logo teñido, y los quince se dibujaron juntos
  // antes de aceptarlos: dos se corrigieron en ese paso (ver Vino y Turquesa).
  // La distancia de tono sola NO basta para decidir si dos colores se parecen.
  {
    // Vino: el ROJO que faltaba. Ojo con la frontera: acá es AMBIENTE, y el
    // rojo de urgencia clínica sigue siendo `clinical-critical`, que ningún tema
    // toca. En oscuro tira al coral a propósito: un rojo aclarado sobre fondo
    // negro se vuelve rosa solo, y ahí se confundía con el Rosado oscuro.
    id: 'vino',
    nombre: 'Vino',
    claro: {
      paper: '#FCF6F5', paperWarm: '#F6EAE8', surface: '#F7ECEA', surface2: '#F0DEDB',
      edge: '#E6CFCB', edgeStrong: '#9B6E68', ink: '#301618', inkMuted: '#6E4A48',
      inkSubtle: '#5D3B3A', inkFaint: '#A98A87', accent: '#A32233', accentHover: '#801827',
      accentLight: '#C4515E',
    },
    oscuro: {
      paper: '#1A0E10', paperWarm: '#221416', surface: '#231517', surface2: '#2D1C1F',
      edge: '#3D272A', edgeStrong: '#5C393D', ink: '#F0E0E1', inkMuted: '#BE9A9C',
      inkSubtle: '#9D7B7D', inkFaint: '#6B4B4E', accent: '#EE8E7E', accentHover: '#F8ABA0',
      accentLight: '#F8ABA0',
    },
  },
  {
    // Mostaza: el dorado. Es el vecino más cercano de Café en la rueda (13
    // grados), así que se separa por saturación y no por tono: Mostaza es oro,
    // Café es tierra. Verificado dibujando los quince, no solo calculando.
    id: 'mostaza',
    nombre: 'Mostaza',
    claro: {
      paper: '#FBF7EC', paperWarm: '#F5EEDC', surface: '#F6F0DF', surface2: '#EDE3CB',
      edge: '#E0D3B6', edgeStrong: '#94815A', ink: '#2C2412', inkMuted: '#6B5A32',
      inkSubtle: '#5A4B29', inkFaint: '#A5925F', accent: '#8A6410', accentHover: '#6B4C06',
      accentLight: '#AD8330',
    },
    oscuro: {
      paper: '#17130A', paperWarm: '#1E190F', surface: '#1F1A10', surface2: '#282114',
      edge: '#392F1C', edgeStrong: '#4F4227', ink: '#F0E8D6', inkMuted: '#BCAC8A',
      inkSubtle: '#9A8B6C', inkFaint: '#68593C', accent: '#D9B45F', accentHover: '#EBC97E',
      accentLight: '#EBC97E',
    },
  },
  {
    // Oliva: verde tierra, el hueco entre el amarillo y el verde saturado de
    // Bosque. Es el más despejado de los cinco: 45 grados del más cercano.
    id: 'oliva',
    nombre: 'Oliva',
    claro: {
      paper: '#F7F8F0', paperWarm: '#EFF1E4', surface: '#F0F2E5', surface2: '#E5E8D3',
      edge: '#D7DBC2', edgeStrong: '#8A9068', ink: '#232614', inkMuted: '#5C6136',
      inkSubtle: '#4D522D', inkFaint: '#9AA075', accent: '#5A6B1E', accentHover: '#445214',
      accentLight: '#7E8F3C',
    },
    oscuro: {
      paper: '#111308', paperWarm: '#17190D', surface: '#181A0E', surface2: '#202312',
      edge: '#2F331B', edgeStrong: '#434826', ink: '#E7EBD5', inkMuted: '#AFB68C',
      inkSubtle: '#8F966E', inkFaint: '#5F663F', accent: '#AEC05E', accentHover: '#C6D683',
      accentLight: '#C6D683',
    },
  },
  {
    // Fucsia: el magenta entre el violeta de Morado y el rosa de Rosado. Los
    // tres se distinguen de cerca; si alguien suma un cuarto por esta zona, hay
    // que volver a dibujarlos juntos antes de aceptarlo.
    id: 'fucsia',
    nombre: 'Fucsia',
    claro: {
      paper: '#FCF5FB', paperWarm: '#F5E9F3', surface: '#F6EBF4', surface2: '#EEDCEB',
      edge: '#E3CDDF', edgeStrong: '#966C90', ink: '#2C1429', inkMuted: '#6A4565',
      inkSubtle: '#593755', inkFaint: '#A483A0', accent: '#9B1F8E', accentHover: '#7A166F',
      accentLight: '#BB4FAE',
    },
    oscuro: {
      paper: '#160C15', paperWarm: '#1D121C', surface: '#1E131D', surface2: '#271A26',
      edge: '#382537', edgeStrong: '#54384F', ink: '#EFDEED', inkMuted: '#BC97B7',
      inkSubtle: '#9B7896', inkFaint: '#6A4966', accent: '#E187D6', accentHover: '#EFA6E5',
      accentLight: '#EFA6E5',
    },
  },
  {
    // Turquesa: cian azulado. Es la zona más apretada de la paleta (Clínica
    // es verde-teal y Celeste es azul), así que este se empujó hacia el azul
    // hasta que los tres se distinguen a simple vista. No devolverlo hacia el
    // verde sin volver a compararlo con Clínica.
    id: 'turquesa',
    nombre: 'Turquesa',
    claro: {
      paper: '#F1FAFB', paperWarm: '#E4F2F4', surface: '#E6F4F5', surface2: '#D4E9EC',
      edge: '#C3DDE1', edgeStrong: '#6A9096', ink: '#10262A', inkMuted: '#3E6167',
      inkSubtle: '#345257', inkFaint: '#7A9EA4', accent: '#0C7391', accentHover: '#05586F',
      accentLight: '#3F95B0',
    },
    oscuro: {
      paper: '#08171A', paperWarm: '#0D1E21', surface: '#0E1F22', surface2: '#14292D',
      edge: '#1F3A3F', edgeStrong: '#2C4E54', ink: '#D9EDF0', inkMuted: '#8DB4BA',
      inkSubtle: '#6F949A', inkFaint: '#4A6A70', accent: '#4FC3E8', accentHover: '#7FD6F0',
      accentLight: '#7FD6F0',
    },
  },
  {
    // Dieciocho (2026-09-17). Los colores de la bandera para Fiestas Patrias.
    // A diferencia de los otros nueve, este NO espera que lo elijan: del 17 al
    // 19 de septiembre `temaDelDia` (lib/dieciocho.js) lo aplica solo a quien
    // nunca eligió tema a mano, que es casi todo el mundo. Decisión del
    // fundador: "si yo quiero que se ambiente con 18, los colores, todo".
    // Quien SÍ eligió un tema conserva el suyo, y elegir cualquiera (incluido
    // MiPAE) es la forma de salirse.
    // El rojo de la bandera NO entra en la paleta: en la app el rojo es
    // urgencia clínica y `clinical-*` no lo toca ningún tema. El rojo vive
    // solo como decoración (`--bandera-roja`: la estrella y los banderines).
    id: 'dieciocho',
    nombre: 'Dieciocho',
    claro: {
      paper: '#FCFDFF', paperWarm: '#EFF4FD', surface: '#EAF1FC', surface2: '#DCE7F8',
      edge: '#C4D5EE', edgeStrong: '#7B90B4', ink: '#0D2140', inkMuted: '#54658A',
      inkSubtle: '#425274', inkFaint: '#7B90B4', accent: '#0039A6', accentHover: '#002A7C',
      accentLight: '#3A60C0',
    },
    oscuro: {
      paper: '#060F1E', paperWarm: '#0B1628', surface: '#0B1628', surface2: '#122239',
      edge: '#1F3658', edgeStrong: '#2F4E79', ink: '#DEE7F4', inkMuted: '#94A9C6',
      inkSubtle: '#7B93B4', inkFaint: '#4E678C', accent: '#7EA0E6', accentHover: '#9BB8F0',
      accentLight: '#9BB8F0',
    },
  },
];

export function temaHabilitado() {
  return process.env.REACT_APP_TEMA_ENABLED !== 'false';
}

// "#4A5FA6" → "74 95 166" (canales separados por espacio: formato que
// Tailwind consume en rgb(var(--accent-rgb) / <alpha>)).
export function hexARgb(hex) {
  const limpio = String(hex).replace('#', '');
  const r = parseInt(limpio.slice(0, 2), 16);
  const g = parseInt(limpio.slice(2, 4), 16);
  const b = parseInt(limpio.slice(4, 6), 16);
  return `${r} ${g} ${b}`;
}

function rgba(hex, alpha) {
  // alpha se imprime con el mismo formato con que se escribió (0.10, no 0.1):
  // mismo estilo textual que index.css usa hoy para estos valores.
  return `rgba(${hexARgb(hex).split(' ').join(', ')}, ${alpha.toFixed(2)})`;
}

// Las 54 variables que este catálogo puede llegar a escribir sobre <html>:
// las mismas que hoy definen el ambiente en index.css (:root/.dark, líneas
// 105-140, 172-199 y 441-531). Cualquier variable fuera de esta lista
// (clinical/shift/module/system/priority/plus/brand a secas) no se toca
// jamás; `--color-brand` SÍ está permitida porque es el slate de selección
// de SegmentedControl/DxClause, no el marino `--brand` del encabezado.
export const VARIABLES_TEMA = [
  // Hex legado (26)
  '--paper', '--paper-warm', '--surface', '--surface-2', '--card-gradient',
  '--edge', '--edge-strong', '--edge-2', '--ink', '--ink-dark', '--ink-muted',
  '--ink-subtle', '--ink-faint', '--accent', '--accent-hover', '--accent-light',
  '--accent-bg', '--accent-border', '--ui-inverse', '--ui-inverse-hover',
  '--scrollbar-track', '--scrollbar-thumb', '--focus-ring', '--focus-glow',
  '--glow-1', '--glow-2',
  // Canales RGB (11)
  '--paper-rgb', '--paper-warm-rgb', '--surface-rgb', '--edge-rgb',
  '--edge-strong-rgb', '--ink-rgb', '--ink-muted-rgb', '--ink-subtle-rgb',
  '--accent-rgb', '--accent-hover-rgb', '--accent-light-rgb',
  // Tokens nuevos (17)
  '--color-brand', '--color-brand-tint', '--color-accent', '--color-accent-hover',
  '--color-accent-bg', '--color-accent-border', '--color-paper', '--color-paper-warm',
  '--color-surface', '--color-surface-2', '--color-ink', '--color-ink-muted',
  '--color-ink-subtle', '--color-ink-faint', '--color-edge', '--color-edge-strong',
  '--color-ui-inverse',
  // Marca (5). Entraron el 2026-09-19 cuando el fundador decidió que el logo
  // se tiña con el tema; el porqué vive junto a su derivación más arriba. Los
  // prohibidos de siempre (clinical/shift/module/system/priority/plus y el
  // marino `--brand` a secas) siguen fuera y su test sigue vigilándolos.
  '--marca-principal', '--marca-acompanante',
  '--sello-principal', '--sello-acompanante', '--sello-linea',
];

export function temaPorId(id) {
  return TEMAS.find((t) => t.id === id) || null;
}

// jsonb/localStorage adulterado jamás rompe: cualquier valor fuera del
// catálogo cae al default.
export function normalizarTema(id) {
  return temaPorId(id) ? id : TEMA_DEFAULT;
}

// null cuando el tema normalizado es el default: index.css ya define esos
// valores, no hay nada que sobrescribir.
export function variablesTema(id, { oscuro } = {}) {
  const normalizado = normalizarTema(id);
  if (normalizado === TEMA_DEFAULT) return null;
  const entrada = temaPorId(normalizado);
  const m = oscuro ? entrada.oscuro : entrada.claro;

  return {
    '--paper': m.paper,
    '--color-paper': m.paper,
    '--paper-warm': m.paperWarm,
    '--color-paper-warm': m.paperWarm,
    '--surface': m.surface,
    '--color-surface': m.surface,
    '--surface-2': m.surface2,
    '--color-surface-2': m.surface2,
    '--edge': m.edge,
    '--color-edge': m.edge,
    '--edge-strong': m.edgeStrong,
    '--color-edge-strong': m.edgeStrong,
    '--edge-2': m.edgeStrong,
    '--ink': m.ink,
    '--ink-dark': m.ink,
    '--color-ink': m.ink,
    '--ink-muted': m.inkMuted,
    '--color-ink-muted': m.inkMuted,
    '--ink-subtle': m.inkSubtle,
    '--color-ink-subtle': m.inkSubtle,
    '--ink-faint': m.inkFaint,
    '--color-ink-faint': m.inkFaint,
    '--accent': m.accent,
    '--color-accent': m.accent,
    '--accent-hover': m.accentHover,
    '--color-accent-hover': m.accentHover,
    '--accent-light': m.accentLight,

    '--card-gradient': `linear-gradient(180deg, ${m.paper} 0%, ${m.surface} 100%)`,

    '--accent-bg': rgba(m.accent, oscuro ? 0.12 : 0.10),
    '--color-accent-bg': rgba(m.accent, oscuro ? 0.12 : 0.10),
    '--color-brand-tint': rgba(m.accent, oscuro ? 0.12 : 0.10),
    '--accent-border': rgba(m.accent, oscuro ? 0.30 : 0.35),
    '--color-accent-border': rgba(m.accent, oscuro ? 0.30 : 0.35),

    // Espejo de index.css: en claro --color-brand es el hover (#3D4D7A), en
    // oscuro es el base (#6A7BA8).
    '--color-brand': oscuro ? m.accent : m.accentHover,

    // LA MARCA SIGUE AL TEMA (decisión del fundador, 2026-09-19: "sigue
    // viéndose el mipae muy azul en el home"). Hasta hoy el logo se pintaba A
    // MEDIAS: la palabra "MiPAE" del encabezado usa `text-brand`, o sea
    // `--color-brand`, que SÍ estaba en esta lista, y las dos figuras de al
    // lado usaban `--marca-*`, que no. Con el tema Café la palabra salía café
    // y las figuras seguían azul marino. Media cosa se nota más que ninguna,
    // que es exactamente el reclamo de Maca sobre los fondos de módulo, una
    // capa más adentro.
    //
    // Ojo con lo que esto corrige: el comentario que justificaba permitir
    // `--color-brand` decía que era "el slate de SegmentedControl, no el
    // marino del encabezado". Era falso, y esa creencia dejó el logo partido.
    //
    // Lo que se tiñe es la marca DENTRO de la app. El ícono de Google Play,
    // el lockup de los correos (`public/brand/mipae-lockup.png`) y el material
    // de tienda son imágenes estáticas: ningún tema las toca, así que el
    // reconocimiento de marca hacia afuera queda igual.
    //
    // El reparto calca la relación que ya tenía el par escrito a mano:
    // `principal` es el tono de MÁS contraste contra el papel en cada modo y
    // `acompanante` el medio. Los tres salen de valores que el test de
    // contraste ya valida, así que ningún tema deja el logo ilegible.
    '--marca-principal': m.accentHover,
    '--marca-acompanante': oscuro ? m.accent : m.accentLight,

    // El SELLO (pantalla de carga y landing) es la misma marca en otra
    // superficie, así que sigue al mismo par: norma de coherencia máxima del
    // 2026-07-16, un cambio de marca aplica en TODAS las superficies. Conserva
    // variables propias porque en el tema DEFAULT sus navies son distintos a
    // los del lockup por historia (#142B4D contra #143B63) y el default no
    // escribe nada, así que ahí se siguen viendo exactamente como siempre. En
    // cualquier otro tema los dos convergen, que es lo que se pidió.
    '--sello-principal': m.accentHover,
    '--sello-acompanante': oscuro ? m.accent : m.accentLight,
    '--sello-linea': oscuro ? m.accentLight : m.accent,

    '--ui-inverse': oscuro ? m.surface2 : m.ink,
    '--color-ui-inverse': oscuro ? m.surface2 : m.ink,
    '--ui-inverse-hover': oscuro ? m.edge : m.inkSubtle,

    '--scrollbar-track': m.surface2,
    '--scrollbar-thumb': m.edgeStrong,

    '--focus-ring': oscuro ? m.accentHover : m.accent,
    '--focus-glow': rgba(m.accent, 0.13),
    '--glow-1': rgba(m.accent, 0.27),
    '--glow-2': rgba(m.accent, 0.53),

    '--paper-rgb': hexARgb(m.paper),
    '--paper-warm-rgb': hexARgb(m.paperWarm),
    '--surface-rgb': hexARgb(m.surface),
    '--edge-rgb': hexARgb(m.edge),
    '--edge-strong-rgb': hexARgb(m.edgeStrong),
    '--ink-rgb': hexARgb(m.ink),
    '--ink-muted-rgb': hexARgb(m.inkMuted),
    '--ink-subtle-rgb': hexARgb(m.inkSubtle),
    '--accent-rgb': hexARgb(m.accent),
    '--accent-hover-rgb': hexARgb(m.accentHover),
    '--accent-light-rgb': hexARgb(m.accentLight),
  };
}

export function leerTema() {
  if (!temaHabilitado()) return TEMA_DEFAULT;
  try {
    return normalizarTema(localStorage.getItem(CLAVE_TEMA));
  } catch {
    return TEMA_DEFAULT;
  }
}

// ¿El usuario eligió tema A MANO alguna vez? No es lo mismo que estar en el
// default: sin elección el ambiente del Dieciocho puede entrar solo, y elegir
// cualquier tema (incluido MiPAE) es justamente cómo se sale de él.
export function hayTemaElegido() {
  try {
    return localStorage.getItem(CLAVE_TEMA) != null;
  } catch {
    return false; // localStorage caído: se trata como "sin elección".
  }
}

export function guardarTema(id) {
  const normalizado = normalizarTema(id);
  try {
    localStorage.setItem(CLAVE_TEMA, normalizado);
  } catch { /* noop: localStorage caído no debe romper el ajuste */ }
  return normalizado;
}

// El color de la barra del sistema/PWA (meta theme-color) sigue al papel del
// tema activo. Con el default son los mismos literales que ThemeContext usa
// hoy (el HTML attribute parser no resuelve var(--paper)).
export function colorBarraSistema(id, { oscuro } = {}) {
  const normalizado = normalizarTema(id);
  if (normalizado === TEMA_DEFAULT) return oscuro ? '#070d1a' : '#ffffff';
  return (oscuro ? temaPorId(normalizado).oscuro : temaPorId(normalizado).claro).paper;
}

// Ratio de contraste WCAG 2.x entre dos colores hex. Se exporta para que el
// TEST fije la accesibilidad de la paleta sin duplicar la fórmula acá.
export function contrasteWcag(hexA, hexB) {
  const luminancia = (hex) => {
    const [r, g, b] = hexARgb(hex).split(' ').map(Number).map((c) => {
      const canal = c / 255;
      return canal <= 0.03928 ? canal / 12.92 : Math.pow((canal + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  const l1 = luminancia(hexA);
  const l2 = luminancia(hexB);
  const [claro, oscuro] = l1 >= l2 ? [l1, l2] : [l2, l1];
  return (claro + 0.05) / (oscuro + 0.05);
}

// Aplica (o limpia) las 54 variables del tema sobre <html>, marca `data-tema`
// (gancho para remapear clases crudas en CSS, ej. `.bg-white`) y sincroniza
// el meta theme-color. Con el tema default o el flag apagado, LIMPIA todo
// para que index.css/.dark vuelvan a mandar sin residuo de una elección
// previa.
export function aplicarTemaEnDocumento(id, { oscuro, root, doc } = {}) {
  try {
    const elemento = root || document.documentElement;
    const documento = doc || document;
    const normalizado = temaHabilitado() ? normalizarTema(id) : TEMA_DEFAULT;
    const variables = temaHabilitado() ? variablesTema(id, { oscuro }) : null;

    if (!variables) {
      VARIABLES_TEMA.forEach((v) => elemento.style.removeProperty(v));
      delete elemento.dataset.tema;
    } else {
      VARIABLES_TEMA.forEach((v) => elemento.style.setProperty(v, variables[v]));
      elemento.dataset.tema = normalizado;
    }

    const meta = documento.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', colorBarraSistema(normalizado, { oscuro }));

    return variables;
  } catch {
    return null;
  }
}
